import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCustomQuery } from './useCustomQuery';
import { connectionsApi } from '../api';
import { useSession } from '@/lib/auth-client';
import { queryKeys as connectionQueryKeys } from './useConnections';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TrackedComponent {
  componentId: string;
  kind: string;
  name: string;
  trackedAt: string;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const componentTrackingKeys = {
  connection: (objectId: number, objectType: string) => ['component-tracking', objectType, objectId] as const
};

// ---------------------------------------------------------------------------
// Hook: useComponentTracking
// ---------------------------------------------------------------------------

/**
 * Reusable hook for tracking components on any object (via connections).
 *
 * Fetches the current user's connection to the given object and exposes:
 * - `trackedComponentIds`: Set<string> for O(1) lookup
 * - `trackedComponents`: full array with metadata
 * - `toggleTracking`: mutation to add/remove a component
 * - `isComponentTracked`: helper to check a single ID
 */
export function useComponentTracking(objectId, objectType) {
  const $ = _c(44);
  const {
    data: session
  } = useSession();
  const userId = session?.user?.id ? Number(session.user.id) : undefined;
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== objectId || $[1] !== objectType) {
    t0 = componentTrackingKeys.connection(objectId, objectType);
    $[0] = objectId;
    $[1] = objectType;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const queryKey = t0;
  let t1;
  if ($[3] !== objectId || $[4] !== objectType || $[5] !== userId) {
    t1 = async () => {
      if (!userId) {
        return null;
      }
      const response = await connectionsApi.getConnections({
        fromType: "user",
        fromId: userId,
        toType: objectType,
        toId: objectId,
        limit: 1
      });
      const conn = response.connections[0] ?? null;
      return conn;
    };
    $[3] = objectId;
    $[4] = objectType;
    $[5] = userId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const t2 = !!userId && !!objectId;
  let t3;
  if ($[7] !== queryKey || $[8] !== t1 || $[9] !== t2) {
    t3 = {
      queryKey,
      queryFn: t1,
      enabled: t2,
      staleTime: 30000
    };
    $[7] = queryKey;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const {
    data: connectionData,
    isLoading
  } = useCustomQuery(t3);
  let t4;
  let trackedComponents;
  if ($[11] !== connectionData?.data) {
    trackedComponents = (connectionData?.data as any)?.trackedComponents ?? [];
    t4 = new Set(trackedComponents.map(_temp));
    $[11] = connectionData?.data;
    $[12] = t4;
    $[13] = trackedComponents;
  } else {
    t4 = $[12];
    trackedComponents = $[13];
  }
  const trackedComponentIds = t4;
  let t5;
  if ($[14] !== objectId || $[15] !== objectType) {
    t5 = params => connectionsApi.trackComponent({
      objectId,
      objectType,
      ...params
    });
    $[14] = objectId;
    $[15] = objectType;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== objectId || $[18] !== objectType || $[19] !== queryClient || $[20] !== queryKey || $[21] !== userId) {
    t6 = async params_0 => {
      await queryClient.cancelQueries({
        queryKey
      });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old => {
        if (!old) {
          return {
            id: 0,
            fromId: userId,
            fromType: "user",
            toId: objectId,
            toType: objectType,
            tags: [],
            systemTags: [],
            data: {
              trackedComponents: params_0.tracked ? [{
                componentId: params_0.componentId,
                kind: params_0.kind,
                name: params_0.name,
                trackedAt: new Date().toISOString()
              }] : []
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
        }
        const existingData = old.data as any || {};
        const existing = existingData.trackedComponents || [];
        let updated;
        if (params_0.tracked) {
          const alreadyTracked = existing.some(t_0 => t_0.componentId === params_0.componentId);
          updated = alreadyTracked ? existing : [...existing, {
            componentId: params_0.componentId,
            kind: params_0.kind,
            name: params_0.name,
            trackedAt: new Date().toISOString()
          }];
        } else {
          updated = existing.filter(t_1 => t_1.componentId !== params_0.componentId);
        }
        return {
          ...old,
          data: {
            ...existingData,
            trackedComponents: updated
          }
        };
      });
      return {
        previous
      };
    };
    $[17] = objectId;
    $[18] = objectType;
    $[19] = queryClient;
    $[20] = queryKey;
    $[21] = userId;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  let t8;
  if ($[23] !== queryClient || $[24] !== queryKey) {
    t7 = (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    };
    t8 = () => {
      queryClient.invalidateQueries({
        queryKey
      });
      queryClient.invalidateQueries({
        queryKey: connectionQueryKeys.connections
      });
    };
    $[23] = queryClient;
    $[24] = queryKey;
    $[25] = t7;
    $[26] = t8;
  } else {
    t7 = $[25];
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t5 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8) {
    t9 = {
      mutationFn: t5,
      onMutate: t6,
      onError: t7,
      onSettled: t8
    };
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const toggleMutation = useMutation(t9);
  let t10;
  if ($[32] !== trackedComponentIds) {
    t10 = componentId => trackedComponentIds.has(componentId);
    $[32] = trackedComponentIds;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const isComponentTracked = t10;
  let t11;
  if ($[34] !== isComponentTracked || $[35] !== toggleMutation) {
    t11 = (componentId_0, kind, name) => {
      const currentlyTracked = isComponentTracked(componentId_0);
      toggleMutation.mutate({
        componentId: componentId_0,
        kind,
        name,
        tracked: !currentlyTracked
      });
    };
    $[34] = isComponentTracked;
    $[35] = toggleMutation;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const toggleTracking = t11;
  let t12;
  if ($[37] !== isComponentTracked || $[38] !== isLoading || $[39] !== toggleMutation.isPending || $[40] !== toggleTracking || $[41] !== trackedComponentIds || $[42] !== trackedComponents) {
    t12 = {
      trackedComponents,
      trackedComponentIds,
      isComponentTracked,
      toggleTracking,
      isLoading,
      isToggling: toggleMutation.isPending
    };
    $[37] = isComponentTracked;
    $[38] = isLoading;
    $[39] = toggleMutation.isPending;
    $[40] = toggleTracking;
    $[41] = trackedComponentIds;
    $[42] = trackedComponents;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  return t12;
}
function _temp(t) {
  return t.componentId;
}